import React from "react";
import { useFormikContext } from "formik";
import { FormTextarea } from "../../components/form";
import { useStorefrontSettings, StorefrontSettingsProvider } from "./StorefrontSettingsProvider";
import SettingList from "./SettingList";
import { useAuth } from "../../hooks";
import { ExclamationCircleIcon } from "@heroicons/react/24/outline";
import { Alert } from "../../components/primitives";
import { AdminUI } from "../../ui/AdminUI";
import EmptyPage from "../EmptyPage/EmptyPage";
import { FormInput } from "../../components/form";
import { Badge, buttonVariants } from "../../components/primitives";
import { cn } from "../../lib/utils";
import { FormikImageField } from "./FormikImageField";
import { SectionRail } from "./SectionRail";


/** "meadowmarket.com/seller/fern-filament" — the address without its scheme. */
const readableUrl = (url: string): string => url.replace(/^https?:\/\//, '');

/**
 * Everything before the handle in the seller's public address, so a handle
 * being typed can be shown against its real host.
 */
const storefrontBase = (url?: string | null): string | undefined =>
    url ? readableUrl(url).replace(/\/[^/]*$/, '/') : undefined;

/**
 * Where the seller's page lives, said as a sentence with the address in it.
 *
 * The spike leads the screen with this; we led with a form field. A seller
 * opening "Storefront" is usually checking whether the page is up and where it
 * is, and neither fact was anywhere on the screen.
 */
const StorefrontStatus: React.FC<{ handle?: string | null; storefrontUrl?: string | null }> = ({
    handle,
    storefrontUrl,
}) => {
    if (!handle) return null;

    return (
        // The shared message component, not a hand-rolled card with a green
        // dot in it. This is a success message about the state of the thing on
        // screen, which is what Alert is, and building it separately meant it
        // missed the tint every other message in the app now carries.
        <Alert
            variant="success"
            title="Your store page is live"
            className="mb-8"
            action={
                /* The address, and a way to go there. It used to be the path
                   on its own, because nothing carried the storefront's host —
                   `Seller.storefrontUrl` does now, so the line names the whole
                   address and opens it. */
                storefrontUrl ? (
                    <a
                        href={storefrontUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs font-medium text-primary hover:underline"
                    >
                        {readableUrl(storefrontUrl)}
                    </a>
                ) : (
                    <span className="text-xs font-medium text-foreground">/seller/{handle}</span>
                )
            }
        >
            Customers can visit it from every product you sell.
        </Alert>
    );
};

/**
 * The storefront page's own contents list.
 *
 * The spike replaces the settings rail with this on the storefront
 * destination — the page is long enough that "where is the banner setting"
 * needs an answer that isn't scrolling.
 */
const StorefrontSubRail: React.FC<{
    sections: { id: string; label: string }[];
    storefrontUrl?: string | null;
}> = ({ sections, storefrontUrl }) => (
    /*
      A contents list into one long page, drawn as one.

      It was already a list of anchors, but it looked exactly like the settings
      rail beside it — which is real navigation — and it marked nothing as
      current. So clicking "Banner" left every field on screen and nothing said
      you had arrived: a tab strip whose tabs appeared broken. Raised twice in
      review, the second time as "the storefront rail which you've done nothing
      about". SectionRail marks where you are as you scroll, which is the whole
      difference between "these are pages" and "these are places in this page".
    */
    <SectionRail sections={sections}>
        {/* Under the contents, where the design puts it. The one thing a
            seller editing this page wants next is to see the page. */}
        {storefrontUrl && (
            <a
                href={storefrontUrl}
                target="_blank"
                rel="noreferrer"
                className={cn(
                    buttonVariants({ variant: 'outline', size: 'sm' }),
                    'w-full justify-center',
                )}
            >
                Preview store page
            </a>
        )}
    </SectionRail>
);

/**
 * The address the handle in the field will produce.
 *
 * It reads the live field value, not the saved one: the point of showing the
 * address under the input is to see what a handle you are part-way through
 * typing will give you, and against the saved value it never moved.
 *
 * The host comes from the seller's own `storefrontUrl` with its last segment
 * removed, so the sentence names the real address rather than a bare path. It
 * falls back to the path alone before there is a published page to point at.
 */
const HandlePreview: React.FC<{ fallback?: string | null; base?: string }> = ({
    fallback,
    base,
}) => {
    const { values } = useFormikContext<{ handle?: string | null }>();
    const handle = (values.handle ?? '').trim() || fallback || 'your-handle';
    return (
        <>
            Your page: {base ?? '/seller/'}
            <b className="font-semibold text-foreground">{handle}</b>
        </>
    );
};

const General: React.FC = () => {
    const settings = useStorefrontSettings();

    return (
        <SettingList.Section
            id="general"
            title="General"
            description="What customers see. Your legal company name lives in Business settings."
        >
            {/* Two short fields on one row, as drawn. */}
            <div className="col-span-6 sm:col-span-3">
                <FormInput
                    name={`storeTitle`}
                    autoComplete="off"
                    type="text"
                    label="Store title"
                    // Not a name: a title. The screen asked for three names in
                    // three fields and left the reader to work out which was
                    // which from the labels alone.
                    help="A title rather than a name — “JJ’s Best Widget Store”."
                    disabled={settings.updating}
                />
            </div>
            <div className="col-span-6 sm:col-span-3">
                <FormInput
                    name={`tagline`}
                    autoComplete="off"
                    type="text"
                    label="Tagline"
                    disabled={settings.updating}
                />
            </div>
            <div className="col-span-6">
                <FormInput
                    name={`handle`}
                    displayValidStatus
                    loading={settings.loadingHandleExists}
                    validStatusIgnore={settings.seller?.handle}
                    autoComplete="off"
                    type="text"
                    label="Handle"
                    disabled={settings.updating}
                    prefix="@"
                    className="max-w-[220px]"
                    trailing={
                        settings.handleAvailable ? (
                            <Badge variant="success" dot>
                                Available
                            </Badge>
                        ) : undefined
                    }
                    help={
                        <HandlePreview
                            fallback={settings.seller?.handle}
                            base={storefrontBase(settings.seller?.storefrontUrl)}
                        />
                    }
                />
            </div>
            <div className="col-span-6">
                <FormInput
                    name={`displayName`}
                    displayValidStatus
                    loading={settings.loadingDisplayNameExists}
                    validStatusIgnore={settings.seller?.displayName}
                    autoComplete="off"
                    type="text"
                    label="Store name"
                    help="Your trading name, the one customers know you by — “JJ’s Widgets”. Unique across the marketplace."
                    disabled={settings.updating}
                />
            </div>
            <div className="col-span-6">
                <FormikImageField
                    name="logo"
                    label="Logo"
                    imageUrl={settings.seller?.logo?.url}
                    requiredWidth={500}
                    requiredHeight={500}
                    disabled={settings.updating}
                    testId="storefront-logo"
                />
            </div>
        </SettingList.Section>
    );
}

const Notification: React.FC = () => {
    const settings = useStorefrontSettings();

    return (
        <SettingList.Section
            id="notification"
            title="Notification"
            description="A message shown across the top of your page."
        >
            <div className="col-span-6">
                <FormTextarea
                    rows={8}
                    className="resize-none"
                    name="notification"
                    autoComplete="off"
                    aria-label="Message"
                    disabled={settings.updating}
                    maxLength={500}
                    showCounter
                />
            </div>
        </SettingList.Section>
    );
};

const Story: React.FC = () => {
    const settings = useStorefrontSettings();

    return (
        <SettingList.Section
            id="story"
            title="Story"
            description="A short introduction at the top of your page."
        >
            <div className="col-span-6">
                <FormInput
                    name={`headline`}
                    autoComplete="off"
                    type="text"
                    label="Headline"
                    disabled={settings.updating}
                />
            </div>
            <div className="col-span-6">
                <FormTextarea
                    rows={8}
                    className="resize-none"
                    name="story"
                    autoComplete="off"
                    label="Story"
                    disabled={settings.updating}
                    maxLength={500}
                    showCounter
                />
            </div>
        </SettingList.Section>
    );
};

const OwnerProfile: React.FC = () => {
    const settings = useStorefrontSettings();

    return (
        <SettingList.Section
            id="owner"
            title="Owner profile"
            description="Who's behind the store. Shown alongside your story."
        >
            <div className="col-span-6">
                <FormInput
                    name={`ownerName`}
                    autoComplete="off"
                    type="text"
                    label="Name"
                    disabled={settings.updating}
                />
            </div>

            <div className="col-span-6">
                <FormikImageField
                    name="profileImage"
                    label="Owner photo"
                    imageUrl={settings.seller?.profileImage?.url}
                    requiredWidth={500}
                    requiredHeight={500}
                    disabled={settings.updating}
                    testId="storefront-profile-image"
                />
            </div>
            <div className="col-span-6">
                <FormTextarea
                    rows={8}
                    className="resize-none"
                    name="bio"
                    autoComplete="off"
                    label="Bio"
                    disabled={settings.updating}
                    maxLength={500}
                    showCounter
                />
            </div>

        </SettingList.Section>
    );
}

const Banner: React.FC = () => {
    const settings = useStorefrontSettings();

    return (
        <SettingList.Section
            id="banner"
            title="Banner"
            description="1200×160 or larger. Shown across the top of your page."
            showFooterActions={false}
        >
            <div className="col-span-6">
                <FormikImageField
                    name="coverImage"
                    label="Banner"
                    hideLabel
                    shape="wide"
                    imageUrl={settings.seller?.coverImage?.url}
                    requiredWidth={1200}
                    requiredHeight={160}
                    disabled={settings.updating}
                    testId="storefront-banner"
                />
            </div>
        </SettingList.Section>
    );
}




/**
 * Every storefront section on one scrolling page.
 *
 * These were five tabs — General, Story, Owner Details, Notification, Banner —
 * each holding two or three fields, so setting up a storefront meant five
 * round trips through a tab strip and five separate saves. They are short
 * enough to read in one pass, and one save now covers the lot. The bare
 * /settings/storefront URL renders this page; previously it matched no tab and
 * showed "Page Not Found".
 */
export const StorefrontSettings: React.FC = () => {

    const auth = useAuth();
    const storefrontEnabled = auth?.user?.seller?.settings.storefront?.enabled;
    const options = auth?.user?.seller?.settings.storefront?.storefrontOptions;
    const allowStory = options?.allowStory;
    const allowOwnerBio = options?.allowOwnerBio;
    const allowNotification = options?.allowNotification;
    const allowBanner = options?.allowBanner;

    if (!storefrontEnabled) {
        return <AdminUI><EmptyPage
            title="Feature not enabled"
            description={`This feature is not enabled.`}
            icon={ExclamationCircleIcon}
        />
        </AdminUI>;
    }

    // One field config covering every section shown, because one save covers
    // every section shown.
    const config: Record<string, boolean> = {
        storeTitle: true,
        tagline: true,
        logo: true,
        handle: true,
        displayName: true,
        ...(allowStory && { headline: true, story: true }),
        ...(allowOwnerBio && { bio: true, ownerName: true, profileImage: true }),
        ...(allowNotification && { notification: true }),
        ...(allowBanner && { coverImage: true }),
    };

    const AllSections: React.FC = () => (
        <>
            <StorefrontStatus
                handle={auth?.user?.seller?.handle}
                storefrontUrl={auth?.user?.seller?.storefrontUrl}
            />
            <General />
            {allowStory && <Story />}
            {allowOwnerBio && <OwnerProfile />}
            {allowNotification && <Notification />}
            {allowBanner && <Banner />}
        </>
    );

    // The page is called Storefront, not Settings — the spike titles each
    // destination by what it is, and every one of these said "Settings".
    const sections = [
        { id: 'general', label: 'General' },
        ...(allowStory ? [{ id: 'story', label: 'Story' }] : []),
        ...(allowOwnerBio ? [{ id: 'owner', label: 'Owner profile' }] : []),
        ...(allowNotification ? [{ id: 'notification', label: 'Notification' }] : []),
        ...(allowBanner ? [{ id: 'banner', label: 'Banner' }] : []),
    ];

    return (
        <StorefrontSettingsProvider
            title="Storefront"
            fieldConfig={config}
            rail={
                <StorefrontSubRail
                    sections={sections}
                    storefrontUrl={auth?.user?.seller?.storefrontUrl}
                />
            }
        >
            <AllSections />
        </StorefrontSettingsProvider>
    );
};
